import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Icon from './icons/Icon';
import DatePicker from './DatePicker';
import { createTokyoDateTime, getTokyoDateParts } from '../utils/dateUtils';
import './InputDate.css';

type InputDateVariant = 'default' | 'ghost';

interface InputDateProps {
  /** 見た目のバリエーション */
  variant?: InputDateVariant;
  /** デフォルト値（現地時間のISO文字列またはDateオブジェクト） */
  defaultValue?: string | Date;
  /** 値（制御されたコンポーネント用） */
  value?: string | Date;
  /** 変更時のコールバック（現地時間のISO文字列） */
  onChange?: (value: string | null) => void;
  /** 日付選択時にカレンダーを閉じるか（デフォルト: 閉じる） */
  closeOnSelect?: boolean;
  /** フォーカス時のコールバック */
  onFocus?: () => void;
  /** ブラー時のコールバック */
  onBlur?: () => void;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー状態 */
  error?: boolean;
  /** 必須項目 */
  required?: boolean;
  /** 名前属性 */
  name?: string;
  /** ID属性 */
  id?: string;
  /** 追加のクラス名 */
  className?: string;
  /** aria-label */
  'aria-label'?: string;
  /** aria-describedby */
  'aria-describedby'?: string;
}

interface InputDateRef {
  focus: () => void;
  blur: () => void;
  getValue: () => string | null;
  setValue: (value: string | Date | null) => void;
}

const InputDate = forwardRef<InputDateRef, InputDateProps>(({
  variant = 'default',
  defaultValue,
  value,
  onChange,
  closeOnSelect = true,
  onFocus,
  onBlur,
  disabled = false,
  error = false,
  required = false,
  name,
  id,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) => {
  // 日付の管理（現地時間）
  const [internalDate, setInternalDate] = useState<Date | null>(() => {
    const initialValue = value || defaultValue;
    if (initialValue) {
      return initialValue instanceof Date ? initialValue : new Date(initialValue);
    }
    return null;
  });

  // カレンダーポップアップの表示状態
  const [showCalendar, setShowCalendar] = useState(false);
  
  // フォーカス状態の管理
  const [isFocused, setIsFocused] = useState(false);
  
  // 各フィールドの参照
  const yearInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const dayInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // 制御されたコンポーネントとしての値更新
  useEffect(() => {
    if (value !== undefined) {
      const newDate = value instanceof Date ? value : (value ? new Date(value) : null);
      setInternalDate(newDate);
    }
  }, [value]);

  // 外部クリック検出でカレンダーを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showCalendar && 
          containerRef.current && 
          !containerRef.current.contains(target) &&
          calendarRef.current &&
          !calendarRef.current.contains(target)) {
        setShowCalendar(false);
        setIsFocused(false);
        if (onBlur) onBlur();
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCalendar, onBlur]);

  // refの公開
  useImperativeHandle(ref, () => ({
    focus: () => {
      yearInputRef.current?.focus();
    },
    blur: () => {
      const activeElement = document.activeElement;
      if (activeElement === yearInputRef.current || 
          activeElement === monthInputRef.current || 
          activeElement === dayInputRef.current) {
        (activeElement as HTMLElement).blur();
      }
    },
    getValue: () => {
      return internalDate ? internalDate.toISOString() : null;
    },
    setValue: (newValue) => {
      const newDate = newValue instanceof Date ? newValue : (newValue ? new Date(newValue) : null);
      setInternalDate(newDate);
      if (onChange) {
        onChange(newDate ? newDate.toISOString() : null);
      }
    }
  }));

  // 日付の更新ハンドラー
  const updateDate = (year?: number, month?: number, day?: number) => {
    const current = getTokyoDateParts(internalDate || new Date());
    const newYear = year !== undefined ? year : current.year;
    const newMonth = month !== undefined ? month - 1 : current.month - 1;
    const newDay = day !== undefined ? day : current.day;
    
    // 有効な日付かチェック
    const newDate = new Date(newYear, newMonth, newDay);
    if (newDate.getFullYear() === newYear && 
        newDate.getMonth() === newMonth && 
        newDate.getDate() === newDay) {
      const tokyoDate = createTokyoDateTime({ year: newYear, month: newMonth + 1, day: newDay });
      setInternalDate(tokyoDate);
      if (onChange) {
        onChange(tokyoDate.toISOString());
      }
    }
  };

  // 年の変更ハンドラー
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const year = parseInt(e.target.value);
    if (!isNaN(year) && year >= 1900 && year <= 2100) {
      updateDate(year);
    }
  };

  // 月の変更ハンドラー
  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const month = parseInt(e.target.value);
    if (!isNaN(month) && month >= 1 && month <= 12) {
      updateDate(undefined, month);
    }
  };

  // 日の変更ハンドラー
  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const day = parseInt(e.target.value);
    if (!isNaN(day) && day >= 1 && day <= 31) {
      updateDate(undefined, undefined, day);
    }
  };

  // キーボードナビゲーション
  const handleKeyDown = (e: React.KeyboardEvent, field: 'year' | 'month' | 'day') => {
    const current = getTokyoDateParts(internalDate || new Date());
    let year = current.year;
    let month = current.month;
    let day = current.day;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (field === 'year') updateDate(year + 1);
      else if (field === 'month') updateDate(year, month + 1 > 12 ? 1 : month + 1);
      else if (field === 'day') updateDate(year, month, day + 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (field === 'year') updateDate(year - 1);
      else if (field === 'month') updateDate(year, month - 1 < 1 ? 12 : month - 1);
      else if (field === 'day') updateDate(year, month, day - 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (field === 'month') yearInputRef.current?.focus();
      else if (field === 'day') monthInputRef.current?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (field === 'year') monthInputRef.current?.focus();
      else if (field === 'month') dayInputRef.current?.focus();
    } else if (e.key === 'Tab') {
      // Tabナビゲーションは標準動作を維持
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowCalendar(!showCalendar);
    }
  };

  // フォーカス管理
  const handleFocus = () => {
    setIsFocused(true);
    setShowCalendar(true);
    if (onFocus) onFocus();
  };

  const handleBlur = (e: React.FocusEvent) => {
    // 関連要素がコンテナ内かチェック
    const relatedTarget = e.relatedTarget as Element;
    if (!containerRef.current?.contains(relatedTarget)) {
      setIsFocused(false);
      setShowCalendar(false);
      if (onBlur) onBlur();
    }
  };

  // カレンダーアイコンクリック
  const handleCalendarClick = () => {
    if (!disabled) {
      setShowCalendar(!showCalendar);
      yearInputRef.current?.focus();
    }
  };

  // 日付の表示値
  const displayParts = internalDate ? getTokyoDateParts(internalDate) : null;
  const displayYear = displayParts ? displayParts.year.toString() : '';
  const displayMonth = displayParts ? displayParts.month.toString().padStart(2, '0') : '';
  const displayDay = displayParts ? displayParts.day.toString().padStart(2, '0') : '';

  return (
    <div 
      ref={containerRef}
      className={`input-date input-date--${variant} ${error ? 'input-date--error' : ''} ${disabled ? 'input-date--disabled' : ''} ${isFocused ? 'input-date--focused' : ''} ${className}`}
      onBlur={handleBlur}
    >
      <div className="input-date__state-layer">
        <div className="input-date__container">
          {/* カレンダーアイコン */}
          <button
            type="button"
            className="input-date__icon"
            onClick={handleCalendarClick}
            disabled={disabled}
            tabIndex={-1}
            aria-label="カレンダーを開く"
          >
            <Icon name="calendar" size={24} />
          </button>

          {/* 日付入力フィールド */}
          <div className="input-date__fields">
            {/* 年 */}
            <input
              ref={yearInputRef}
              type="number"
              className="input-date__field input-date__field--year text-single-large"
              value={displayYear}
              onChange={handleYearChange}
              onKeyDown={(e) => handleKeyDown(e, 'year')}
              onFocus={handleFocus}
              disabled={disabled}
              min="1900"
              max="2100"
              placeholder="YYYY"
              aria-label="年"
              name={name ? `${name}_year` : undefined}
              id={id ? `${id}_year` : undefined}
            />
            
            <span className="input-date__separator">/</span>
            
            {/* 月 */}
            <input
              ref={monthInputRef}
              type="number"
              className="input-date__field input-date__field--month text-single-large"
              value={displayMonth}
              onChange={handleMonthChange}
              onKeyDown={(e) => handleKeyDown(e, 'month')}
              onFocus={handleFocus}
              disabled={disabled}
              min="1"
              max="12"
              placeholder="MM"
              aria-label="月"
              name={name ? `${name}_month` : undefined}
              id={id ? `${id}_month` : undefined}
            />
            
            <span className="input-date__separator">/</span>
            
            {/* 日 */}
            <input
              ref={dayInputRef}
              type="number"
              className="input-date__field input-date__field--day text-single-large"
              value={displayDay}
              onChange={handleDayChange}
              onKeyDown={(e) => handleKeyDown(e, 'day')}
              onFocus={handleFocus}
              disabled={disabled}
              min="1"
              max="31"
              placeholder="DD"
              aria-label="日"
              name={name ? `${name}_day` : undefined}
              id={id ? `${id}_day` : undefined}
            />
          </div>
        </div>

        {/* カレンダーポップアップ */}
        {showCalendar && !disabled && (
          <div ref={calendarRef} className="input-date__calendar">
            <DatePicker
              selectedDate={displayParts ? new Date(displayParts.year, displayParts.month - 1, displayParts.day) : null}
              onDateSelect={(selectedDate) => {
                const tokyoDate = createTokyoDateTime({ year: selectedDate.getFullYear(), month: selectedDate.getMonth() + 1, day: selectedDate.getDate() });
                setInternalDate(tokyoDate);
                if (onChange) {
                  onChange(tokyoDate.toISOString());
                }
                if (closeOnSelect) {
                  setShowCalendar(false); // 日付選択後にカレンダーを閉じる
                }
              }}
              onClose={() => setShowCalendar(false)}
              displayMonth={internalDate || undefined}
            />
          </div>
        )}
      </div>

      {/* 隠しフィールド（フォーム送信用） */}
      <input
        type="hidden"
        name={name}
        value={internalDate ? internalDate.toISOString() : ''}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error}
      />
    </div>
  );
});

InputDate.displayName = 'InputDate';

export default InputDate;
export type { InputDateRef };
